import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography, Switch,
  TextField, Stack, Snackbar, Alert, CircularProgress, IconButton, MenuItem, Paper, Skeleton, Tooltip,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import { salonApi, WorkingHours, ScheduleException } from '../services/salonService';
import { StatusPill, useConfirm } from './ui';
import { tokens } from '../theme';
import { formatDate } from '../utils/format';

// dayOfWeek: 0=Sunday … 6=Saturday (matches backend). Displayed Monday-first.
const DAYS: { dow: number; label: string }[] = [
  { dow: 1, label: 'Poniedziałek' },
  { dow: 2, label: 'Wtorek' },
  { dow: 3, label: 'Środa' },
  { dow: 4, label: 'Czwartek' },
  { dow: 5, label: 'Piątek' },
  { dow: 6, label: 'Sobota' },
  { dow: 0, label: 'Niedziela' },
];

const WEEKDAY_NAMES = ['Niedziela', 'Poniedziałek', 'Wtorek', 'Środa', 'Czwartek', 'Piątek', 'Sobota'];

const weekdayName = (iso: string) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : WEEKDAY_NAMES[d.getDay()];
};

type Row = { dayOfWeek: number; isClosed: boolean; openTime: string; closeTime: string };

const defaultRow = (dow: number): Row => ({
  dayOfWeek: dow,
  isClosed: dow === 0,
  openTime: '09:00',
  closeTime: dow === 6 ? '16:00' : '20:00',
});

export const WorkingHoursEditor: React.FC<{
  salonId: string | null;
  salonName?: string;
  open: boolean;
  onClose: () => void;
  onSaved?: () => void;
}> = ({ salonId, salonName, open, onClose, onSaved }) => {
  const confirm = useConfirm();
  const [rows, setRows] = useState<Record<number, Row>>({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{ msg: string; sev: 'success' | 'error' } | null>(null);
  const [exceptions, setExceptions] = useState<ScheduleException[]>([]);
  const [exForm, setExForm] = useState({ date: '', isClosed: true, openTime: '09:00', closeTime: '16:00', reason: '' });

  const loadExceptions = (id: string) =>
    salonApi.getScheduleExceptions(id).then(setExceptions).catch(() => setExceptions([]));

  useEffect(() => {
    if (!open || !salonId) return;
    setLoading(true);
    loadExceptions(salonId);
    salonApi
      .getWorkingHours(salonId)
      .then((data: WorkingHours[]) => {
        const map: Record<number, Row> = {};
        DAYS.forEach(({ dow }) => {
          const existing = data.find((d) => d.dayOfWeek === dow);
          map[dow] = existing
            ? { dayOfWeek: dow, isClosed: existing.isClosed, openTime: existing.openTime || '09:00', closeTime: existing.closeTime || '20:00' }
            : defaultRow(dow);
        });
        setRows(map);
      })
      .catch(() => {
        const map: Record<number, Row> = {};
        DAYS.forEach(({ dow }) => (map[dow] = defaultRow(dow)));
        setRows(map);
      })
      .finally(() => setLoading(false));
  }, [open, salonId]);

  const addException = async () => {
    if (!salonId || !exForm.date) { setToast({ msg: 'Wybierz datę wyjątku', sev: 'error' }); return; }
    try {
      await salonApi.upsertScheduleException(salonId, {
        date: exForm.date,
        isClosed: exForm.isClosed,
        openTime: exForm.isClosed ? null : exForm.openTime,
        closeTime: exForm.isClosed ? null : exForm.closeTime,
        reason: exForm.reason || null,
      });
      setExForm({ date: '', isClosed: true, openTime: '09:00', closeTime: '16:00', reason: '' });
      await loadExceptions(salonId);
      setToast({ msg: 'Wyjątek zapisany', sev: 'success' });
    } catch (e) {
      setToast({ msg: (e as { response?: { data?: { error?: string } } }).response?.data?.error || 'Nie udało się zapisać wyjątku. Spróbuj ponownie.', sev: 'error' });
    }
  };

  const removeException = async (ex: ScheduleException) => {
    if (!salonId) return;
    const ok = await confirm({
      title: 'Usunąć wyjątek w grafiku oddziału?',
      details: <>{weekdayName(ex.date)}, {formatDate(ex.date)}{ex.reason ? ` · ${ex.reason}` : ''}</>,
      description: 'Tego dnia znów będą obowiązywać zwykłe godziny z grafiku tygodniowego.',
      confirmLabel: 'Usuń wyjątek',
      tone: 'danger',
    });
    if (!ok) return;
    await salonApi.deleteScheduleException(salonId, ex.id);
    await loadExceptions(salonId);
  };

  const update = (dow: number, patch: Partial<Row>) =>
    setRows((prev) => ({ ...prev, [dow]: { ...prev[dow], ...patch } }));

  const copyMondayToWeekdays = () => {
    const mon = rows[1];
    if (!mon) return;
    setRows((prev) => {
      const next = { ...prev };
      [2, 3, 4, 5].forEach((d) => (next[d] = { ...mon, dayOfWeek: d }));
      return next;
    });
  };

  const copyMondayToAll = () => {
    const mon = rows[1];
    if (!mon) return;
    setRows((prev) => {
      const next = { ...prev };
      [0, 2, 3, 4, 5, 6].forEach((d) => (next[d] = { ...mon, dayOfWeek: d }));
      return next;
    });
  };

  const save = async () => {
    if (!salonId) return;
    // basic validation: open days need from < to
    for (const { dow, label } of DAYS) {
      const r = rows[dow];
      if (r && !r.isClosed && r.openTime >= r.closeTime) {
        setToast({ msg: `${label}: godzina zamknięcia musi być późniejsza niż otwarcia`, sev: 'error' });
        return;
      }
    }
    setSaving(true);
    try {
      const payload = DAYS.map(({ dow }) => {
        const r = rows[dow] || defaultRow(dow);
        return { dayOfWeek: dow, isClosed: r.isClosed, openTime: r.openTime, closeTime: r.closeTime };
      });
      await salonApi.setWorkingHours(salonId, payload);
      setToast({ msg: 'Grafik oddziału zapisany', sev: 'success' });
      onSaved?.();
      setTimeout(onClose, 600);
    } catch (e) {
      setToast({ msg: (e as { response?: { data?: { error?: string } } }).response?.data?.error || 'Nie udało się zapisać grafiku. Spróbuj ponownie.', sev: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const sectionTitleSx = { fontWeight: 700, fontSize: '1rem', lineHeight: 1.4 } as const;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth aria-labelledby="working-hours-title">
      <DialogTitle id="working-hours-title">
        Grafik pracy oddziału
        {salonName && (
          <Typography component="span" sx={{ display: 'block', fontSize: '0.9375rem', fontWeight: 500, color: 'text.secondary', mt: 0.25 }}>
            {salonName}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <Stack gap={1.25} sx={{ py: 1 }} aria-busy="true" aria-label="Wczytywanie grafiku">
            {DAYS.map(({ dow }) => <Skeleton key={dow} variant="rounded" height={46} />)}
          </Stack>
        ) : (
          <>
            <Stack alignItems="flex-start" gap={0.5} sx={{ mb: 0.5 }}>
              <Typography sx={sectionTitleSx}>Godziny w zwykłym tygodniu</Typography>
              <Stack direction="row" gap={0.5} flexWrap="wrap" sx={{ ml: -1.5 }}>
                <Button variant="text" size="small" startIcon={<ContentCopyIcon />} onClick={copyMondayToWeekdays}>
                  Poniedziałek na dni robocze
                </Button>
                <Button variant="text" size="small" startIcon={<ContentCopyIcon />} onClick={copyMondayToAll}>
                  Poniedziałek na cały tydzień
                </Button>
              </Stack>
            </Stack>

            <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }}>
              {DAYS.map(({ dow, label }) => {
                const r = rows[dow] || defaultRow(dow);
                const invalid = !r.isClosed && r.openTime >= r.closeTime;
                return (
                  <Box
                    component="li"
                    key={dow}
                    sx={{
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      alignItems: { xs: 'stretch', sm: 'center' },
                      gap: { xs: 1, sm: 2 },
                      py: 1.25,
                      minHeight: 70,
                      borderBottom: `1px solid ${tokens.line}`,
                      '&:last-of-type': { borderBottom: 0 },
                    }}
                  >
                    <Stack direction="row" alignItems="center" gap={1.5} sx={{ width: { sm: 200 }, flexShrink: 0, minWidth: 0 }}>
                      <Switch
                        checked={!r.isClosed}
                        onChange={(e) => update(dow, { isClosed: !e.target.checked })}
                        inputProps={{ 'aria-label': `${label} — otwarte` }}
                      />
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontWeight: 650, fontSize: '0.9375rem', lineHeight: 1.35 }}>{label}</Typography>
                        <Typography variant="caption" component="div" sx={{ color: r.isClosed ? tokens.muted : tokens.success }}>
                          {r.isClosed ? 'Zamknięte' : 'Otwarte'}
                        </Typography>
                      </Box>
                    </Stack>
                    {!r.isClosed && (
                      <Box sx={{ flex: 1, minWidth: 0, pl: { xs: 7.25, sm: 0 } }}>
                        <Stack direction="row" alignItems="center" gap={1}>
                          <TextField
                            type="time"
                            value={r.openTime}
                            onChange={(e) => update(dow, { openTime: e.target.value })}
                            error={invalid}
                            sx={{ flex: 1, minWidth: 0 }}
                            inputProps={{ 'aria-label': `${label}: otwarcie` }}
                          />
                          <Typography aria-hidden sx={{ color: tokens.muted }}>–</Typography>
                          <TextField
                            type="time"
                            value={r.closeTime}
                            onChange={(e) => update(dow, { closeTime: e.target.value })}
                            error={invalid}
                            sx={{ flex: 1, minWidth: 0 }}
                            inputProps={{ 'aria-label': `${label}: zamknięcie` }}
                          />
                        </Stack>
                        {invalid && (
                          <Typography variant="caption" component="div" sx={{ color: tokens.danger, fontWeight: 600, mt: 0.5 }}>
                            Zamknięcie musi być później niż otwarcie
                          </Typography>
                        )}
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Box>

            <Box sx={{ borderTop: `1px solid ${tokens.line}`, mt: 1.5, pt: 2.5 }}>
              <Typography sx={sectionTitleSx}>Wyjątki: dni wolne i inne godziny</Typography>
              <Typography variant="body2" sx={{ color: tokens.muted, mt: 0.25, mb: 1.5 }}>
                Święta, remont, krótszy dzień. Wyjątek zapisuje się od razu po dodaniu.
              </Typography>

              {exceptions.length > 0 && (
                <Box component="ul" sx={{ listStyle: 'none', m: 0, mb: 2, p: 0 }}>
                  {exceptions.map((ex) => (
                    <Stack
                      component="li"
                      key={ex.id}
                      direction="row"
                      alignItems="center"
                      gap={1.5}
                      sx={{ py: 1.25, borderBottom: `1px solid ${tokens.line}`, '&:last-of-type': { borderBottom: 0 } }}
                    >
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontWeight: 650, fontSize: '0.9375rem', fontVariantNumeric: 'tabular-nums' }}>
                          {weekdayName(ex.date)}, {formatDate(ex.date)}
                        </Typography>
                        <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap" sx={{ mt: 0.5 }}>
                          {ex.isClosed ? (
                            <StatusPill label="Zamknięte" tone="neutral" size="small" />
                          ) : (
                            <StatusPill label={`Otwarte ${ex.openTime}–${ex.closeTime}`} tone="plum" size="small" />
                          )}
                          {ex.reason && (
                            <Typography variant="caption" sx={{ color: tokens.muted, minWidth: 0, wordBreak: 'break-word' }}>{ex.reason}</Typography>
                          )}
                        </Stack>
                      </Box>
                      <Tooltip title="Usuń wyjątek">
                        <IconButton onClick={() => removeException(ex)} aria-label="Usuń wyjątek">
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  ))}
                </Box>
              )}

              <Paper variant="outlined" sx={{ p: { xs: 1.75, sm: 2 }, bgcolor: tokens.surface }}>
                <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))' } }}>
                  <TextField
                    type="date"
                    label="Dzień"
                    InputLabelProps={{ shrink: true }}
                    value={exForm.date}
                    onChange={(e) => setExForm({ ...exForm, date: e.target.value })}
                    fullWidth
                    sx={{ minWidth: 170 }}
                  />
                  <TextField
                    select
                    label="Tego dnia"
                    value={exForm.isClosed ? 'closed' : 'open'}
                    onChange={(e) => setExForm({ ...exForm, isClosed: e.target.value === 'closed' })}
                    fullWidth
                  >
                    <MenuItem value="closed">Zamknięte cały dzień</MenuItem>
                    <MenuItem value="open">Otwarte w innych godzinach</MenuItem>
                  </TextField>
                  {!exForm.isClosed && (
                    <Box sx={{ gridColumn: '1 / -1', display: 'grid', gap: 2, gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}>
                      <TextField type="time" label="Otwarcie" InputLabelProps={{ shrink: true }} value={exForm.openTime} fullWidth
                        onChange={(e) => setExForm({ ...exForm, openTime: e.target.value })} />
                      <TextField type="time" label="Zamknięcie" InputLabelProps={{ shrink: true }} value={exForm.closeTime} fullWidth
                        onChange={(e) => setExForm({ ...exForm, closeTime: e.target.value })} />
                    </Box>
                  )}
                  <TextField
                    label="Powód (opcjonalnie)"
                    placeholder="np. święto, inwentaryzacja"
                    value={exForm.reason}
                    onChange={(e) => setExForm({ ...exForm, reason: e.target.value })}
                    fullWidth
                    sx={{ gridColumn: '1 / -1' }}
                  />
                </Box>
                <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2 }}>
                  <Button variant="outlined" startIcon={<AddIcon />} onClick={addException} sx={{ width: { xs: '100%', sm: 'auto' } }}>
                    Dodaj wyjątek
                  </Button>
                </Stack>
              </Paper>
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ borderTop: `1px solid ${tokens.line}`, pt: 1.5 }}>
        <Button variant="text" onClick={onClose}>Anuluj</Button>
        <Button
          variant="contained"
          onClick={save}
          disabled={saving || loading}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {saving ? 'Zapisywanie…' : 'Zapisz grafik'}
        </Button>
      </DialogActions>
      <Snackbar open={!!toast} autoHideDuration={3500} onClose={() => setToast(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        {toast ? <Alert severity={toast.sev} variant="filled" onClose={() => setToast(null)}>{toast.msg}</Alert> : undefined}
      </Snackbar>
    </Dialog>
  );
};

export default WorkingHoursEditor;
